import * as THREE from 'three';
import { Character, type CharacterKind } from '../world/Character';
import { local, MARKET, PLAZA_RADIUS, type Obstacle } from '../world/Market';
import type { Planet } from '../world/Planet';
import { createSeededRandom } from '../utils/random';

/** What crowd folk say when you bump into them. Everyone's arming up. */
const CHATTER = [
  'Did you hear? Lights past the Gate last night.',
  'Prices doubled since the Herald started shouting.',
  'My cousin bought three shields. Three!',
  'They say the hills are moving. Hills don’t move.',
  'Is that... LORD BURNWOOD?!',
  'Corwin’s round shields are the real deal.',
  'If the Gate opens, I’m first in line. Second, maybe.',
  'Hilda says the forge hasn’t been this busy in years.',
  'Watch your purse. Green hoods are trouble.',
  'The Industry won’t know what hit it.',
  'Have you tried Tobin’s buns? Life-changing.',
  'My lord! Give ’em a loot box to the face!',
];

const KINDS: CharacterKind[] = ['Knight', 'Mage', 'Rogue', 'Barbarian'];
/** Props crowd folk might carry: most of the town is shopping for gear. */
const CARRY: Record<CharacterKind, string[][]> = {
  Knight: [['1H_Sword'], ['Round_Shield'], ['1H_Sword', 'Round_Shield'], []],
  Mage: [['Spellbook'], ['1H_Wand'], ['2H_Staff'], []],
  Rogue: [['Knife'], ['1H_Crossbow'], [], []],
  Barbarian: [['Mug'], ['1H_Axe'], ['Barbarian_Round_Shield'], []],
};

export type Walker = {
  char: Character;
  x: number;
  z: number;
  vx: number;
  vz: number;
  tx: number;
  tz: number;
  wait: number;
  speed: number;
  facing: THREE.Vector3;
  line: string;
  /** The pickpocket: flees from the player. */
  thief?: boolean;
};

const tmpTarget = new THREE.Vector3();

export class Crowd {
  readonly walkers: Walker[] = [];
  private readonly rand = createSeededRandom(4242);

  constructor(private readonly planet: Planet, private readonly obstacles: Obstacle[], private readonly scene: THREE.Object3D) {
  }

  async spawn(count: number): Promise<void> {
    for (let i = 0; i < count; i++) {
      const kind = KINDS[i % KINDS.length];
      const carry = CARRY[kind][Math.floor(this.rand() * CARRY[kind].length)];
      const char = await Character.create(kind, { keep: carry, hideHeadgear: this.rand() < 0.5, height: 1.15 + this.rand() * 0.25 });
      const [x, z] = this.freeSpot();
      const w: Walker = { char, x, z, vx: 0, vz: 0, tx: x, tz: z, wait: this.rand() * 4, speed: 1.2 + this.rand() * 0.8, facing: new THREE.Vector3(1, 0, 0), line: CHATTER[i % CHATTER.length] };
      this.scene.add(char.root);
      this.walkers.push(w);
    }
  }

  /** The thief: a quick little rogue who keeps his distance. */
  async spawnThief(): Promise<Walker> {
    const char = await Character.create('Rogue', { keep: ['Knife'], height: 1.1 });
    const [x, z] = this.freeSpot();
    const w: Walker = { char, x, z, vx: 0, vz: 0, tx: x, tz: z, wait: 0, speed: 2.4, facing: new THREE.Vector3(1, 0, 0), line: 'You’ll never catch me!', thief: true };
    char.root.visible = false;
    this.scene.add(char.root);
    this.walkers.push(w);
    return w;
  }

  private freeSpot(): [number, number] {
    for (let tries = 0; tries < 60; tries++) {
      const a = this.rand() * Math.PI * 2;
      const r = 2.5 + Math.sqrt(this.rand()) * (PLAZA_RADIUS - 3);
      const x = Math.cos(a) * r, z = Math.sin(a) * r;
      if (this.obstacles.every((o) => Math.hypot(o.x - x, o.z - z) > o.r + 0.6)) return [x, z];
    }
    return [3, 3];
  }

  worldPos(w: Walker, out = new THREE.Vector3()): THREE.Vector3 {
    return out.copy(local(this.planet, w.x, w.z));
  }

  /**
   * Steering in the plaza's local 2D frame: seek target, separate from other
   * walkers and the player, slide around obstacles. Idle at each stop.
   */
  update(dt: number, t: number, player2d: THREE.Vector2, playerFrozen: boolean, thiefActive: boolean): void {
    for (const w of this.walkers) {
      if (w.thief && !thiefActive) continue;
      let ax = 0, az = 0;
      const pdx = w.x - player2d.x, pdz = w.z - player2d.y;
      const pd = Math.hypot(pdx, pdz);

      if (w.thief) {
        // Run away from the player when close, otherwise loiter and taunt.
        if (pd < 7) {
          w.tx = w.x + (pdx / pd) * 4;
          w.tz = w.z + (pdz / pd) * 4;
          w.wait = 0;
        }
      }
      if (w.wait > 0) {
        w.wait -= dt;
      } else {
        const dx = w.tx - w.x, dz = w.tz - w.z;
        const d = Math.hypot(dx, dz);
        if (d < 0.4) {
          w.wait = w.thief ? 0.6 : 1.5 + this.rand() * 5;
          [w.tx, w.tz] = this.freeSpot();
        } else {
          const sp = w.thief && pd < 7 ? 3.3 : w.speed;
          ax += (dx / d) * sp - w.vx;
          az += (dz / d) * sp - w.vz;
        }
      }
      // Personal space.
      for (const o of this.walkers) {
        if (o === w || (o.thief && !thiefActive)) continue;
        const dx = w.x - o.x, dz = w.z - o.z;
        const d2 = dx * dx + dz * dz;
        if (d2 < 1.0 && d2 > 1e-6) {
          const d = Math.sqrt(d2);
          ax += (dx / d) * (1 - d) * 6;
          az += (dz / d) * (1 - d) * 6;
        }
      }
      if (pd < 1.1 && pd > 1e-4 && !playerFrozen) {
        ax += (pdx / pd) * (1.1 - pd) * 10;
        az += (pdz / pd) * (1.1 - pd) * 10;
      }
      w.vx += ax * Math.min(1, dt * 4);
      w.vz += az * Math.min(1, dt * 4);
      if (w.wait > 0) { w.vx *= 0.85; w.vz *= 0.85; }
      let nx = w.x + w.vx * dt, nz = w.z + w.vz * dt;
      // Stay out of stalls and buildings.
      for (const o of this.obstacles) {
        const dx = nx - o.x, dz = nz - o.z;
        const d = Math.hypot(dx, dz);
        if (d < o.r && d > 1e-4) { nx = o.x + (dx / d) * o.r; nz = o.z + (dz / d) * o.r; }
      }
      // Stay inside the square.
      const rr = Math.hypot(nx, nz);
      if (rr > PLAZA_RADIUS - 0.5) { nx *= (PLAZA_RADIUS - 0.5) / rr; nz *= (PLAZA_RADIUS - 0.5) / rr; }
      w.x = nx;
      w.z = nz;

      const root = w.char.root;
      const pos = this.worldPos(w, root.position);
      const up = pos.clone().normalize();
      const speed = Math.hypot(w.vx, w.vz);
      if (speed > 0.15) {
        tmpTarget.copy(local(this.planet, w.x + w.vx, w.z + w.vz)).sub(pos).projectOnPlane(up).normalize();
        w.facing.lerp(tmpTarget, 1 - Math.exp(-dt * 8)).projectOnPlane(up).normalize();
      }
      const x = new THREE.Vector3().crossVectors(up, w.facing).normalize();
      if (x.lengthSq() > 0.5) root.quaternion.setFromRotationMatrix(new THREE.Matrix4().makeBasis(x, up, w.facing.clone().projectOnPlane(up).normalize()));
      w.char.update(dt, t, { speed, air: false, talking: false });
    }
  }

  /** Nearest visible walker within `range` of a world point. */
  nearest(p: THREE.Vector3, range: number, thiefActive: boolean): Walker | null {
    let best: Walker | null = null;
    let bd = range;
    for (const w of this.walkers) {
      if (w.thief && !thiefActive) continue;
      const d = w.char.root.position.distanceTo(p);
      if (d < bd) { bd = d; best = w; }
    }
    return best;
  }

  /** Make a walker stop and turn toward a point for a moment. */
  hold(w: Walker, seconds: number, toward: THREE.Vector3): void {
    w.wait = Math.max(w.wait, seconds);
    w.vx = w.vz = 0;
    const up = w.char.root.position.clone().normalize();
    w.facing.copy(toward).sub(w.char.root.position).projectOnPlane(up).normalize();
  }

  get centreDir(): THREE.Vector3 {
    return MARKET;
  }
}
